import styled, { css } from 'styled-components'

import { ResponsiveImage, TypeStyle } from '@syconium/little-miss-figgy'

import { Label } from '../../primitives/shared-form-styles'

export type OccupantDividers = 'before' | 'after' | 'around'

const divider = css`
  border-color: ${o => o.theme.color.stroke.neutral.border.decoration.on.background};
  border-style: solid;
  border-width: 0;
`

/** Rows are ruled off from each other, and from what is around the list on the sides asked for. */
export const OccupantList = styled.div<{ $dividers: OccupantDividers }>`
  ${divider}
  border-bottom-width: ${o => (o.$dividers === 'before' ? 0 : '1px')};
  border-top-width: ${o => (o.$dividers === 'after' ? 0 : '1px')};
  display: flex;
  flex-direction: column;
  padding-bottom: ${o => (o.$dividers === 'before' ? 0 : o.theme.spacing(5))};
  padding-top: ${o => (o.$dividers === 'after' ? 0 : o.theme.spacing(5))};
`

export const OccupantRow = styled.div`
  ${divider}
  display: flex;
  flex-direction: column;
  gap: ${o => o.theme.spacing(2)};

  & + & {
    border-top-width: 1px;
    margin-top: ${o => o.theme.spacing(5)};
    padding-top: ${o => o.theme.spacing(5)};
  }
`

export const OccupantHeader = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
`

export const OccupantLabel = styled(Label)`
  line-height: 18px;
  margin-bottom: 0;
`

export const OccupantTrash = styled.button`
  align-items: center;
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  height: 24px;
  justify-content: center;
  padding: 0;
  width: 24px;

  &:focus-visible {
    outline: 2px solid ${o => o.theme.color.effect.focus.outline.on.background};
    outline-offset: 2px;
  }
`

export const OccupantContent = styled.div`
  align-items: center;
  display: flex;
  gap: 10px;
`

/** `color` is what a `currentColor` glyph inside draws in, picked to stand out against the garment. */
export const OccupantTile = styled.div<{ $backgroundColor: string; $color: string }>`
  align-items: center;
  background-color: ${o => o.$backgroundColor};
  color: ${o => o.$color};
  border-radius: 4px;
  display: flex;
  flex-shrink: 0;
  height: 40px;
  justify-content: center;
  overflow: hidden;
  width: 40px;
`

/** A black-and-white logo is drawn in grey, as the preview draws it, so it reads as it will be stitched. */
export const OccupantImage = styled(ResponsiveImage)<{ $grayscale: boolean }>`
  filter: ${o => (o.$grayscale ? 'grayscale(1)' : 'none')};
  height: 25px; /* 40px tile − 2 × 7.5px gap */
  width: 25px;
  object-fit: contain;
`

/** Text embroidery's two lines are joined with a newline, which `pre-line` breaks on. */
export const OccupantText = styled.p`
  ${TypeStyle.css.bodySmall}
  color: ${o => o.theme.color.text.tertiary.on.background};
  flex: 1;
  font-weight: ${o => o.theme.typography.font.weight[600]};
  letter-spacing: 0.65px;
  line-height: 22px;
  margin: 0;
  min-width: 0;
  overflow-wrap: anywhere;
  white-space: pre-line;
`
